import { useCallback, useEffect, useRef } from "react";
import { FileText, X } from "lucide-react";

export const AGREEMENT_PROSE =
  "prose prose-sm max-w-none break-words text-gray-700 " +
  "prose-headings:font-semibold prose-headings:tracking-tight prose-headings:text-gray-900 " +
  "prose-h1:text-xl prose-h2:mb-4 prose-h2:mt-0 prose-h2:text-lg " +
  "prose-h3:mb-1.5 prose-h3:mt-6 prose-h3:text-[13.5px] prose-p:my-2 prose-p:leading-relaxed";

const formatDate = (value) =>
  value ? new Date(value).toLocaleDateString(undefined, { dateStyle: "long" }) : null;

// The vendor agreement in a dialog. Without `onClose` it cannot be dismissed,
// which is how the acceptance prompt keeps a vendor from carrying on.
// `onReadToEnd` fires once the reader has scrolled to the last line.
const AgreementModal = ({
  title = "Vendor Agreement",
  intro,
  html,
  url,
  updatedAt,
  onClose,
  onReadToEnd,
  footer,
}) => {
  const bodyRef = useRef(null);

  const checkEnd = useCallback(() => {
    const el = bodyRef.current;
    if (el && el.scrollTop + el.clientHeight >= el.scrollHeight - 24) onReadToEnd?.();
  }, [onReadToEnd]);

  // A short agreement that needs no scrolling counts as read.
  useEffect(() => {
    checkEnd();
  }, [checkEnd, html]);

  const updated = formatDate(updatedAt);

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-gray-900/60 p-4 backdrop-blur-[2px]">
      <div
        className="flex h-[min(88vh,820px)] w-full max-w-3xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl ring-1 ring-black/5"
        role="dialog"
        aria-modal="true"
        aria-labelledby="agreement-title"
      >
        <div className="flex items-start gap-4 border-b border-gray-100 px-6 py-5">
          <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-brand-50 text-brand-600">
            <FileText size={19} strokeWidth={2} />
          </span>
          <div className="min-w-0 flex-1">
            <h2 id="agreement-title" className="text-[17px] font-semibold tracking-tight text-gray-900">
              {title}
            </h2>
            {intro && <p className="mt-0.5 text-[13px] leading-relaxed text-gray-500">{intro}</p>}
            {(updated || url) && (
              <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-500">
                {updated && <span>Last updated {updated}</span>}
                {url && (
                  <a
                    href={url}
                    target="_blank"
                    rel="noreferrer"
                    className="font-medium text-brand-600 hover:underline"
                  >
                    Open online version
                  </a>
                )}
              </div>
            )}
          </div>
          {onClose && (
            <button
              type="button"
              onClick={onClose}
              className="-mr-1 -mt-1 rounded-lg p-1.5 text-gray-400 transition hover:bg-gray-100 hover:text-gray-600"
              aria-label="Close"
            >
              <X size={18} />
            </button>
          )}
        </div>

        <div ref={bodyRef} onScroll={checkEnd} className="flex-1 overflow-y-auto bg-gray-50 px-4 py-6 sm:px-8">
          <article className="mx-auto max-w-2xl rounded-xl border border-gray-200 bg-white px-6 py-7 shadow-sm sm:px-10 sm:py-9">
            <div className={AGREEMENT_PROSE} dangerouslySetInnerHTML={{ __html: html || "" }} />
          </article>
        </div>

        {footer && <div className="border-t border-gray-100 bg-white px-6 py-4">{footer}</div>}
      </div>
    </div>
  );
};

export default AgreementModal;
